<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue';
import BaseLabel from '@/atoms/BaseLabel.vue';

import contraceptionImg from '@/assets/images/health-women/contraception.svg?url';
import fertiliteImg from '@/assets/images/health-women/fertilite.svg?url';
import materniteImg from '@/assets/images/health-women/maternite.svg?url';
import endometrioseImg from '@/assets/images/health-women/endometriose.svg?url';
import smopImg from '@/assets/images/health-women/smop.svg?url';
import brcaImg from '@/assets/images/health-women/brca.svg?url';
import menopauseImg from '@/assets/images/health-women/menopause.svg?url';
import autreImg from '@/assets/images/health-women/autre.svg?url';
import TextInput from '@/molecules/TextInput.vue';
import { REQUIRED_ERROR_MESSAGE } from '@/helpers/default-validators';

const props = defineProps<{ theme?: string; submitAttempted?: boolean }>();
const modelValue = defineModel<string>({ default: '' });
const emit = defineEmits<{ canSubmit: [boolean] }>();

const KNOWN_THEMES = [
  'contraception',
  'cancer du sein (BRCA)',
  'endométriose',
  'fertilité',
  'maternité',
  'ménopause',
  'syndrome métabolique ovarien polyendocrinien (SMOP)',
];

type ThemeData = { description: string; img: string };

const THEME_MAP: Record<string, ThemeData> = {
  contraception: {
    description: `Retour sur les méthodes de contraception, leurs prises en charge et les professionnels de santé pouvant être consultés dans le cadre d'une démarche contraceptive.`,
    img: contraceptionImg,
  },
  fertilité: {
    description: `Échange autour de la capacité à concevoir un enfant et du cycle menstruel pour aider la patiente à identifier ses périodes de fertilité et mieux comprendre son corps.`,
    img: fertiliteImg,
  },
  maternité: {
    description: `Toutes les étapes de la maternité peuvent être abordées dans le cadre de cet échange selon les envies et besoins de la patiente : aide à la conception, grossesse et post accouchement.`,
    img: materniteImg,
  },
  endométriose: {
    description: `Entretien visant à améliorer les connaissances de la patiente sur l'endométriose (notions clés, symptômes, impacts) et l'orienter vers les moyens de diagnostic et de prise en charge de la maladie selon sa situation personnelle.`,
    img: endometrioseImg,
  },
  'syndrome métabolique ovarien polyendocrinien (SMOP)': {
    description: `Échange autour du syndrome métabolique ovarien polyendocrinien (SMOP) : compréhension du trouble, symptômes, diagnostic et traitements associés.`,
    img: smopImg,
  },
  'cancer du sein (BRCA)': {
    description: `Entretien visant à sensibiliser la patiente sur le cancer du sein (facteurs de risques, recommandations générales, modalités de dépistage) selon sa situation personnelle et à lui proposer une démonstration de l’autopalpation.`,
    img: brcaImg,
  },
  ménopause: {
    description: `Échange autour de la ménopause (voire préménopause) : compréhension, symptômes, effets et répercussions sur la santé, pour aider la patiente à se préparer et bien la vivre au quotidien.`,
    img: menopauseImg,
  },
  autre: {
    description: `Échangez et confirmez avec la patiente la thématique qu'elle souhaite aborder, en prenant soin de sélectionner une thématique <b>unique</b> et <b>clairement</b> identifiée.<br><br>Notez ensuite le nom de la thématique dans le champ ci-dessous :`,
    img: autreImg,
  },
};

const THEME_ALIASES: Record<string, string> = {
  'syndrome des ovaires polykystiques (SOPK)':
    'syndrome métabolique ovarien polyendocrinien (SMOP)',
};

// Theme venant de l'API (via prop ou modelValue initial)
const rawTheme = props.theme ?? modelValue.value;
const apiTheme = THEME_ALIASES[rawTheme] ?? rawTheme;

const isKnownTheme = computed(() => KNOWN_THEMES.includes(apiTheme));

const themeData = computed<ThemeData>(() => THEME_MAP[apiTheme] ?? THEME_MAP['autre']);

const capitalize = (text: string) =>
  text
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

// Thématique saisie par l'utilisateur si le theme est "autre" ou inconnu
const newTheme = ref('');
const touched = ref(false);

const themeError = computed(() =>
  (touched.value || props.submitAttempted) && newTheme.value === ''
    ? REQUIRED_ERROR_MESSAGE
    : undefined,
);

function onBlur() {
  touched.value = true;
}

watch(newTheme, (val) => {
  modelValue.value = val;
  emit('canSubmit', val.length > 0);
});

onMounted(() => {
  emit('canSubmit', isKnownTheme.value);
});
</script>

<template>
  <div class="flex flex-col space-y-4 pb-6">
    <!-- Image thématique -->
    <div class="flex justify-center my-4">
      <img :alt="apiTheme" :src="themeData.img" class="w-48 h-48 object-contain" />
    </div>

    <!-- Titre du thème (si connu) -->
    <BaseLabel v-if="isKnownTheme" :text="capitalize(apiTheme)" preset="medium" weight="bold" />

    <!-- Description -->
    <p class="text-sm text-grey-600" v-html="themeData.description" />

    <!-- Texte d'échange (si thème connu) -->
    <p v-if="isKnownTheme" class="text-sm text-grey-600">
      Échangez avec la patiente pour déterminer si elle souhaite aborder un sujet précis dans le
      cadre de cette thématique ou la parcourir de manière générale.
    </p>

    <!-- Saisie libre si thème "autre" ou non reconnu -->
    <div v-if="!isKnownTheme">
      <TextInput
        id="health-women-report-theme"
        v-model="newTheme"
        maxlength="20"
        placeholder="Indiquer la thématique retenue par le patient"
        :error="themeError"
        :destructive="!!themeError"
        @blur="onBlur"
      />
    </div>
  </div>
</template>
